import { Link } from "react-router-dom";
import {
    Tv,
    Clapperboard,
    MonitorPlay,
    Trophy,
    Smile,
    Sparkles,
    Newspaper,
    Languages,
    Gift,
    ArrowRight,
    BadgePercent,
} from "lucide-react";
import { PageHeader, FadeUp, Counter } from "../components/Reveal";
import { TV_CATEGORIES, OTT_APPS, IMAGES } from "../data/siteData";

const CATEGORY_ICONS = {
    Telugu: Languages,
    Movies: Clapperboard,
    Series: MonitorPlay,
    Sports: Trophy,
    Kids: Smile,
    Entertainment: Sparkles,
    News: Newspaper,
};

const TvShowcase = () => (
    <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="tv-section">
        <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
                <FadeUp>
                    <span
                        data-testid="free-tv-badge"
                        className="inline-flex items-center gap-2 rounded-full bg-[#E61C24] px-4 py-2 font-mono text-[11px] font-bold tracking-[0.22em] text-white shadow-[0_0_34px_rgba(230,28,36,0.5)]"
                    >
                        <Gift className="h-4 w-4" />
                        FREE TV CHANNELS FOR THE FIRST 3 MONTHS
                    </span>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <h2 className="font-display mt-6 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                        <Counter to={240} suffix="+" className="text-gradient-fire" /> Live
                        TV Channels
                    </h2>
                    <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg">
                        Get access to entertainment right from the beginning with
                        selected TV channels and OTT benefits — something for every
                        member of the family.
                    </p>
                </FadeUp>
                <div className="mt-8 flex flex-wrap gap-3">
                    {TV_CATEGORIES.map((c, i) => {
                        const Icon = CATEGORY_ICONS[c];
                        return (
                            <FadeUp key={c} delay={0.12 + i * 0.05}>
                                <span
                                    data-testid={`tv-category-${c.toLowerCase()}`}
                                    className="glass inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-slate-200 transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]"
                                >
                                    <Icon className="h-4 w-4 text-[#ff5a60]" />
                                    {c}
                                </span>
                            </FadeUp>
                        );
                    })}
                </div>
            </div>

            <FadeUp delay={0.15}>
                <div className="relative">
                    <div
                        className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-[#00F0FF]/10 blur-3xl"
                        aria-hidden="true"
                    />
                    <div
                        data-testid="tv-screen-visual"
                        className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-black shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                    >
                        <img
                            src={IMAGES.smartTv}
                            alt="Smart TV streaming entertainment at home"
                            className="aspect-[16/10] w-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30" />
                        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-3 p-5">
                            <div>
                                <p className="font-mono text-[10px] font-bold tracking-[0.28em] text-[#00F0FF]">
                                    NOW STREAMING
                                </p>
                                <p className="font-display mt-1 text-lg font-bold text-white">
                                    240+ Channels · TV + OTT
                                </p>
                            </div>
                            <span className="flex items-center gap-2 rounded-full bg-[#E61C24] px-3 py-1.5 font-mono text-[10px] font-bold tracking-widest text-white">
                                <span className="animate-pulse-dot h-2 w-2 rounded-full bg-white" />
                                LIVE
                            </span>
                        </div>
                        <div className="absolute left-1/2 top-4 flex -translate-x-1/2 gap-2">
                            {TV_CATEGORIES.slice(0, 4).map((c) => (
                                <span
                                    key={c}
                                    className="rounded-full bg-black/55 px-3 py-1 text-[11px] font-semibold text-slate-200 backdrop-blur-md"
                                >
                                    {c}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="mx-auto h-3 w-40 rounded-b-3xl bg-[#131927]" aria-hidden="true" />
                </div>
            </FadeUp>
        </div>
    </section>
);

const OttApps = () => (
    <section className="border-y border-white/10 bg-[#0d1220] py-20 sm:py-24" data-testid="ott-apps-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <FadeUp>
                <p className="font-mono text-xs font-semibold tracking-[0.3em] text-[#00F0FF]">
                    OTT APPS
                </p>
                <h2 className="font-display mt-3 max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                    Your Favourite OTT Platforms, All in One Place
                </h2>
                <p className="mt-3 font-mono text-sm font-semibold tracking-widest text-slate-400">
                    <Counter to={14} suffix="+" className="text-white" /> OTT APPS INCLUDED
                </p>
            </FadeUp>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {OTT_APPS.map((app, i) => (
                    <FadeUp key={app.name} delay={i * 0.06}>
                        <div
                            data-testid={`ott-app-${app.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="glass group flex h-full flex-col items-center gap-3 rounded-3xl p-6 text-center transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-white/25"
                        >
                            <span
                                className="flex h-14 w-14 items-center justify-center rounded-2xl font-display text-2xl font-black text-white shadow-lg transition-transform duration-300 group-hover:scale-110"
                                style={{ backgroundColor: app.color }}
                                aria-hidden="true"
                            >
                                {app.letter}
                            </span>
                            <span className="text-sm font-semibold text-slate-200">
                                {app.name}
                            </span>
                        </div>
                    </FadeUp>
                ))}
                <FadeUp delay={OTT_APPS.length * 0.06}>
                    <div
                        data-testid="ott-app-more"
                        className="flex h-full flex-col items-center justify-center gap-2 rounded-3xl border border-dashed border-[#E61C24]/50 bg-[#E61C24]/8 p-6 text-center"
                    >
                        <span className="font-display text-2xl font-black text-[#ff6b70]">
                            +9
                        </span>
                        <span className="text-sm font-semibold text-slate-300">
                            More OTT Platforms
                        </span>
                    </div>
                </FadeUp>
            </div>
        </div>
    </section>
);

const OttPricing = () => (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8" data-testid="ott-pricing-section">
        <FadeUp>
            <h2 className="font-display max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                OTT Pack Pricing
            </h2>
            <p className="mt-3 max-w-xl text-base text-slate-400">
                One pack. 14+ OTT apps. Big savings for your whole family.
            </p>
        </FadeUp>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
            <FadeUp delay={0.05}>
                <div
                    data-testid="ott-pack-6m"
                    className="glass relative flex h-full flex-col overflow-hidden rounded-3xl p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-[#00F0FF]/40"
                >
                    <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#00F0FF]/10 px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.24em] text-[#00F0FF]">
                        <BadgePercent className="h-3.5 w-3.5" />
                        SAVE ₹500
                    </span>
                    <p className="font-display mt-5 text-2xl font-bold text-white">
                        6 Months
                    </p>
                    <p className="mt-3 flex items-baseline gap-3">
                        <span className="font-mono text-4xl font-black text-white">₹3,200</span>
                        <span className="font-mono text-lg text-slate-500 line-through">₹3,700</span>
                    </p>
                    <p className="mt-2 text-sm text-slate-400">
                        14+ OTT apps bundled for six months of entertainment.
                    </p>
                    <Link
                        to="/get-connected"
                        data-testid="ott-pack-6m-cta"
                        className="group mt-7 inline-flex w-fit items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-[border-color,color,transform] duration-200 hover:scale-105 hover:border-[#00F0FF]/70 hover:text-[#00F0FF]"
                    >
                        Get This Pack
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </div>
            </FadeUp>

            <FadeUp delay={0.12}>
                <div
                    data-testid="ott-pack-1y"
                    className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-[#E61C24]/60 bg-gradient-to-b from-[#E61C24]/18 to-[#131927] p-8 shadow-[0_0_54px_rgba(230,28,36,0.22)] transition-transform duration-300 hover:-translate-y-1.5"
                >
                    <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#E61C24] px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.24em] text-white">
                        <Tv className="h-3.5 w-3.5" />
                        BEST VALUE
                    </span>
                    <p className="font-display mt-5 text-2xl font-bold text-white">
                        1 Year
                    </p>
                    <p className="mt-3 font-mono text-4xl font-black text-white">₹6,000</p>
                    <p className="mt-2 text-sm text-slate-400">
                        A full year of 14+ OTT apps — the smartest entertainment deal
                        for your home.
                    </p>
                    <Link
                        to="/get-connected"
                        data-testid="ott-pack-1y-cta"
                        className="group mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-[#E61C24] px-6 py-3 text-sm font-extrabold text-white shadow-[0_10px_36px_rgba(230,28,36,0.45)] transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-[#C7151C]"
                    >
                        Get This Pack
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </div>
            </FadeUp>
        </div>
    </section>
);

export default function OTT() {
    return (
        <>
            <PageHeader
                testId="ott-header"
                eyebrow="OTT & ENTERTAINMENT"
                title={
                    <>
                        One Connection.{" "}
                        <span className="text-gradient-fire">Endless Entertainment.</span>
                    </>
                }
                subtitle="TV channels and OTT entertainment options for the whole family — right from day one."
            />
            <TvShowcase />
            <OttApps />
            <OttPricing />
        </>
    );
}
